import React from 'react';

type Character = 'OPERATOR' | 'PFY' | 'USER' | 'MANAGEMENT';

interface DialogueBubbleProps {
  character: Character;
  text: string;
  side?: 'left' | 'right';
}

const CHARACTER_CONFIG = {
  OPERATOR: {
    name: 'The Operator',
    avatar: '/operator-avatar.png',
    accent: 'border-red-500 shadow-red-500',
    borderColor: 'border-red-500',
    shadowGlow: 'shadow-glow-red',
    nameColor: 'text-red-400',
  },
  PFY: {
    name: 'The PFY',
    avatar: '/pfy-avatar.png',
    accent: 'border-teal-500 shadow-teal-500',
    borderColor: 'border-teal-500',
    shadowGlow: 'shadow-glow-teal',
    nameColor: 'text-teal-400',
  },
  USER: {
    name: 'User',
    avatar: '/user-avatar.png',
    accent: 'border-amber-500 shadow-amber-500',
    borderColor: 'border-amber-500',
    shadowGlow: 'shadow-glow-amber',
    nameColor: 'text-amber-400',
  },
  MANAGEMENT: {
    name: 'Management',
    avatar: '/management-avatar.png',
    accent: 'border-purple-500 shadow-purple-500',
    borderColor: 'border-purple-500',
    shadowGlow: 'shadow-glow-purple',
    nameColor: 'text-purple-400',
  },
};

export const DialogueBubble: React.FC<DialogueBubbleProps> = ({
  character,
  text,
  side = 'left',
}) => {
  const config = CHARACTER_CONFIG[character];
  const isLeft = side === 'left';

  return (
    <div
      className={`flex items-start gap-4 my-28 animate-fade-in ${
        isLeft ? 'flex-row' : 'flex-row-reverse'
      }`}
      role="figure"
      aria-label={`${config.name} says: ${text}`}
      style={{ border: 'none', borderLeft: 'none', paddingLeft: 0, marginLeft: 0 }}
    >
      {/* Character Avatar */}
      <div className="flex-shrink-0 flex flex-col items-center gap-2">
        <div
          className={`w-16 h-16 rounded-lg overflow-hidden shadow-lg border-2 ${config.accent}`}
        >
          <img
            src={config.avatar}
            alt={config.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <span className={`text-xs font-medium ${config.nameColor}`}>
          {config.name}
        </span>
      </div>

      {/* Speech Bubble */}
      <div
        className={`relative -mt-8 ${isLeft ? 'text-left' : 'text-right'}`}
      >
        {/* Bubble Tail */}
        <div
          className={`absolute top-4 ${
            isLeft ? '-left-2' : '-right-2'
          } w-0 h-0 border-t-8 border-b-8 ${
            isLeft ? 'border-r-8' : 'border-l-8'
          } border-transparent ${
            isLeft ? 'border-r-black' : 'border-l-black'
          }`}
          aria-hidden="true"
        />

        {/* Bubble Content */}
        <div
          className={`inline-block bg-black rounded px-4 pt-4 pb-2 border-2 ${config.borderColor} ${config.shadowGlow}`}
          style={{ maxWidth: '500px' }}
        >
          <p className="text-gray-100 leading-tight text-sm font-mono italic">"{text}"</p>
        </div>
      </div>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
export default React.memo(DialogueBubble);
